import { Star } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { listFavouriteJobIds, addFavourite, removeFavourite } from "@/lib/favourites";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface Props {
  jobId: string;
  size?: "sm" | "md";
  className?: string;
}

export function FavouriteButton({ jobId, size = "md", className }: Props) {
  const { profile } = useAuth();
  const qc = useQueryClient();
  const userId = profile?.id;

  const { data: ids = [] } = useQuery({
    queryKey: ["favourites", userId],
    queryFn: () => (userId ? listFavouriteJobIds(userId) : Promise.resolve([])),
    enabled: !!userId,
  });

  const isFav = ids.includes(jobId);

  const mut = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Sign in required");
      if (isFav) await removeFavourite(userId, jobId);
      else await addFavourite(userId, jobId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["favourites", userId] });
      toast.success(isFav ? "Removed from favourites" : "Added to favourites");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  if (!userId) return null;
  const dim = size === "sm" ? "h-4 w-4" : "h-5 w-5";

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        mut.mutate();
      }}
      disabled={mut.isPending}
      aria-pressed={isFav}
      aria-label={isFav ? "Remove from favourites" : "Add to favourites"}
      className={cn(
        "inline-flex items-center justify-center rounded-full p-1.5 transition-colors",
        "hover:bg-muted",
        isFav ? "text-amber-500" : "text-muted-foreground",
        className,
      )}
    >
      <Star className={cn(dim, isFav && "fill-current")} />
    </button>
  );
}
